/** Dependency-free connectivity state (shared by axios, onlineCheckService, offlineDB). */

/**
 * @typedef {'checking' | 'online' | 'offline' | 'backend-unavailable' | 'expired-session'} ConnectivityStatus
 *
 * The three degraded states are deliberately distinct, because the app owes the
 * operator a different answer and a different recovery for each:
 *
 * - `offline`             the transport failed — `fetch` rejected, or the browser
 *                         reports no interface. Nothing reached a server.
 * - `backend-unavailable` a response *arrived* and was not 2xx (502/503/504, or a
 *                         session-lock timeout). The network is proven fine, so
 *                         "Offline" would be a lie and reads may keep retrying.
 * - `expired-session`     the network and the backend are both fine; this
 *                         session is not. The answer is "sign in", never
 *                         "you are offline", and the cache must survive it.
 *
 * `checking` means no verdict has ever been reached — boot only. It is never
 * re-entered once a verdict exists, because every consumer reads it as offline
 * and a recurring probe must not blank the UI on its own schedule.
 */

const STATUSES = new Set(['checking', 'online', 'offline', 'backend-unavailable', 'expired-session']);

let status = /** @type {ConnectivityStatus} */ ('checking');

/**
 * The last status that was an actual verdict — never `checking`.
 *
 * This is what makes the offline→online edge derivable instead of declared. The
 * edge used to be decided by a `transitionFrom` argument each caller passed in,
 * and a caller that pre-wrote `checking` before probing (onNetworkError) turned
 * every recovered blip into a fabricated reconnect — which then re-ran a
 * thirteen-table fan-out, a user refetch and a POS stock resync. Provenance that
 * callers can get wrong is provenance the state should own.
 */
let lastConfirmed = /** @type {ConnectivityStatus|null} */ (null);

/** Orthogonal to `status`: a probe being in flight is not a state of the world. */
let probing = false;

export function getConnectivityStatus() {
    return status;
}

/** The last real verdict, or null before the first one. Never `checking`. */
export function getLastConfirmedStatus() {
    return lastConfirmed;
}

export function setConnectivityStatusRaw(next) {
    if (!STATUSES.has(next)) return false;
    status = next;
    if (next !== 'checking') lastConfirmed = next;
    return true;
}

export function setProbing(next) {
    probing = Boolean(next);
}

export function isProbing() {
    return probing;
}

/**
 * Reset to the pre-boot state. Test seam and `onlineCheckService.destroy()` —
 * without it `lastConfirmed` would leak a previous suite's verdict into the next
 * one and suppress a legitimate reconnect edge.
 */
export function resetConnectivityState() {
    status = 'checking';
    lastConfirmed = null;
    probing = false;
}

/** Confirmed backend reachability. False while checking or in any degraded state. */
export function canMutateOnline() {
    return status === 'online';
}

/** The server answered us — true even when this session is no longer valid. */
export function isBackendReachable() {
    return status === 'online' || status === 'expired-session';
}

/** A settled verdict that is not `online`. False during boot's `checking`. */
export function isDegraded() {
    return status === 'offline' || status === 'backend-unavailable' || status === 'expired-session';
}
